import React, { useState, useEffect, useCallback } from "react";
import { getFunctionUrl } from "common";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon
} from "@mui/icons-material";
import CircularLoading from "../components/CircularLoading";
import { MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

export default function Msg91Templates() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [message, setMessage] = useState("");
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState("");
  const [templateForm, setTemplateForm] = useState({
    name: "",
    templateId: "",
    useCase: "login"
  });

  const auth = useSelector((state) => state.auth);
  const msg91TemplatesUrl = getFunctionUrl("msg91templates");
  const msg91AnalyticsUrl = getFunctionUrl("msg91analytics");
  const testMsg91TemplateUrl = getFunctionUrl("testMsg91Template");

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(msg91TemplatesUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const result = await response.json();
      if (result.success) {
        setTemplates(result.data || []);
      } else {
        throw new Error(result.error || "Failed to load templates");
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      setMessage(t("error_loading_templates"));
    } finally {
      setLoading(false);
    }
  }, [msg91TemplatesUrl, t]);

  const loadAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      setAnalyticsError("");
      const response = await fetch(msg91AnalyticsUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const result = await response.json();
      if (result.success) {
        setAnalyticsData(result.data);
      } else {
        throw new Error(result.error || "Failed to load analytics");
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      setAnalyticsError(error.message || "Failed to load analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [msg91AnalyticsUrl]);

  // Load templates and analytics from database
  useEffect(() => {
    loadTemplates();
    loadAnalytics();
  }, [loadTemplates, loadAnalytics]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({ ...template });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: "",
        templateId: "",
        useCase: "login"
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      templateId: "",
      useCase: "login"
    });
  };

  const handleSaveTemplate = async () => {
    // Validate required fields
    if (!templateForm.name.trim()) {
      setMessage("Template name is required");
      return;
    }
    if (!templateForm.templateId.trim()) {
      setMessage("Template ID is required");
      return;
    }
    if (!templateForm.useCase.trim()) {
      setMessage("Use Case is required");
      return;
    }
    
    try {
      if (editingTemplate) {
        // Update existing template
        const response = await fetch(msg91TemplatesUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...templateForm,
            id: editingTemplate.id,
            action: 'update'
          })
        });
        const result = await response.json();
        
        if (result.success) {
          const updatedTemplates = templates.map(t => 
            t.id === editingTemplate.id ? { ...templateForm, id: t.id } : t
          );
          setTemplates(updatedTemplates);
          setMessage(t("template_updated"));
        } else {
          throw new Error(result.error || "Failed to update template");
        }
      } else {
        // Add new template
        const response = await fetch(msg91TemplatesUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...templateForm,
            action: 'create'
          })
        });
        const result = await response.json();
        
        if (result.success) {
          const newTemplate = {
            ...templateForm,
            id: result.data.id
          };
          setTemplates([...templates, newTemplate]);
          setMessage(t("template_added"));
        } else {
          throw new Error(result.error || "Failed to add template");
        }
      }
      
      handleCloseDialog();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving template:", error);
      setMessage(t("error_saving_template"));
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm(t("confirm_delete"))) {
      try {
        const response = await fetch(msg91TemplatesUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: templateId,
            action: 'delete'
          })
        });
        const result = await response.json();
        
        if (result.success) {
          const updatedTemplates = templates.filter(t => t.id !== templateId);
          setTemplates(updatedTemplates);
          setMessage(t("template_deleted"));
          setTimeout(() => setMessage(""), 3000);
        } else {
          throw new Error(result.error || "Failed to delete template");
        }
      } catch (error) {
        console.error("Error deleting template:", error);
        setMessage(t("error_deleting_template"));
      }
    }
  };

  const handleOpenTestDialog = (template) => {
    setTestingTemplate(template);
    setTestPhone("");
    setTestResult("");
    setTestSending(false);
    setOpenTestDialog(true);
  };

  const handleCloseTestDialog = () => {
    setOpenTestDialog(false);
    setTestingTemplate(null);
    setTestPhone("");
    setTestResult("");
    setTestSending(false);
  };

  const handleTestTemplate = async () => {
    if (!testPhone.trim()) {
      setTestResult("Please enter a phone number");
      return;
    }

    setTestSending(true);
    setTestResult("");
    
    try {
      const response = await fetch(testMsg91TemplateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: testingTemplate.templateId,
          mobile: testPhone,
          useCase: testingTemplate.useCase
        })
      });
      const result = await response.json();
      
      if (result.success) {
        setTestResult("✅ Test SMS sent successfully! Check your phone.");
      } else {
        setTestResult(`❌ Error: ${result.error || "Failed to send test SMS"}`);
      }
    } catch (error) {
      console.error("Error testing template:", error);
      setTestResult("❌ Error: Failed to send test SMS");
    } finally {
      setTestSending(false);
    }
  };

  // Check if user is admin
  if (!auth.profile || auth.profile.usertype !== "admin") {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: Only Admin can manage MSG91 templates.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <CircularLoading />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom style={{ color: MAIN_COLOR, fontFamily: FONT_FAMILY }}>
              {t('msg91_templates')}
            </Typography>
            <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY }} color="textSecondary">
              {t('template_management')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              style={{ backgroundColor: MAIN_COLOR, fontFamily: FONT_FAMILY }}
            >
              {t('add_template')}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Message */}
        {message && (
          <Alert
            severity={message.includes("Error") ? "error" : "success"}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        )}

        {/* Analytics Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom style={{ fontFamily: FONT_FAMILY }}>
                📊 OTP Analytics
              </Typography>
              <Button
                variant="outlined"
                onClick={loadAnalytics}
                disabled={analyticsLoading}
                size="small"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {analyticsLoading ? "Loading..." : "Refresh"}
              </Button>
            </Box>

            {analyticsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {analyticsError}
              </Alert>
            )}

            {analyticsLoading ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularLoading />
              </Box>
            ) : analyticsData ? (
              <Box>
                {/* Summary Cards */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ minHeight: '70px' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1, px: 1 }}>
                        <Typography variant="h6" color="primary" style={{ fontFamily: FONT_FAMILY }}>
                          {analyticsData.total?.message || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontFamily: FONT_FAMILY }}>
                          Total
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ minHeight: '70px' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1, px: 1 }}>
                        <Typography variant="h6" color="success.main" style={{ fontFamily: FONT_FAMILY }}>
                          {analyticsData.total?.delivered || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontFamily: FONT_FAMILY }}>
                          Delivered ({analyticsData.total?.message > 0 ? 
                            Math.round((analyticsData.total.delivered / analyticsData.total.message) * 100) : 0}%)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ minHeight: '70px' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1, px: 1 }}>
                        <Typography variant="h6" color="error.main" style={{ fontFamily: FONT_FAMILY }}>
                          {analyticsData.total?.failed || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontFamily: FONT_FAMILY }}>
                          Failed ({analyticsData.total?.message > 0 ? 
                            Math.round((analyticsData.total.failed / analyticsData.total.message) * 100) : 0}%)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ minHeight: '70px' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1, px: 1 }}>
                        <Typography variant="h6" color="primary" style={{ fontFamily: FONT_FAMILY }}>
                          ₹{analyticsData.total?.totalCredits?.toFixed(1) || '0.0'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontFamily: FONT_FAMILY }}>
                          Cost
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Daily Breakdown Table */}
                <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, mb: 1 }}>
                  📅 Last 7 Days
                </Typography>
                <TableContainer sx={{ maxHeight: '200px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>Date</TableCell>
                        <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>Sent</TableCell>
                        <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>✅</TableCell>
                        <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>❌</TableCell>
                        <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>%</TableCell>
                        <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>₹</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.data?.map((row) => (
                        <TableRow key={row.date} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            {new Date(row.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </TableCell>
                          <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            {row.sent}
                          </TableCell>
                          <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            <span style={{ color: '#4caf50' }}>{row.delivered}</span>
                          </TableCell>
                          <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            <span style={{ color: row.failed > 0 ? '#f44336' : '#666' }}>
                              {row.failed}
                            </span>
                          </TableCell>
                          <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            {row.sent > 0 ? Math.round((row.delivered / row.sent) * 100) : 0}%
                          </TableCell>
                          <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', py: 0.5 }}>
                            {row.balanceDeducted?.toFixed(1)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ fontFamily: FONT_FAMILY }}>
                Click "Refresh" to load analytics data
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Templates Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom style={{ fontFamily: FONT_FAMILY }}>
              {t('template_list')}
            </Typography>

            {templates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" style={{ fontFamily: FONT_FAMILY }}>
                  {t('no_templates')}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontFamily: FONT_FAMILY }}>Template Name</TableCell>
                      <TableCell style={{ fontFamily: FONT_FAMILY }}>Template ID</TableCell>
                      <TableCell style={{ fontFamily: FONT_FAMILY }}>Use Case</TableCell>
                      <TableCell style={{ fontFamily: FONT_FAMILY }}>Created Date</TableCell>
                      <TableCell style={{ fontFamily: FONT_FAMILY }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          {template.name}
                        </TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          {template.templateId}
                        </TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          <Chip
                            label={template.useCase}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          {template.createdAt ? new Date(template.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          }) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Test Template">
                              <IconButton 
                                onClick={() => handleOpenTestDialog(template)}
                                color="success"
                                size="small"
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Template">
                              <IconButton 
                                onClick={() => handleOpenDialog(template)}
                                color="primary"
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Template">
                              <IconButton 
                                onClick={() => handleDeleteTemplate(template.id)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Template Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          disableRestoreFocus
        >
          <DialogTitle style={{ fontFamily: FONT_FAMILY }}>
            {editingTemplate ? t('edit_template') : t('add_template')}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  variant="outlined"
                  placeholder="e.g., OTP Login, Booking Confirmed"
                  InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="MSG91 Template ID"
                  value={templateForm.templateId}
                  onChange={(e) => setTemplateForm({ ...templateForm, templateId: e.target.value })}
                  variant="outlined"
                  placeholder="e.g., 68b993a370d8d96753676962"
                  InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                  helperText="Get this ID from your MSG91 dashboard"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontFamily: FONT_FAMILY }}>Use Case</InputLabel>
                  <Select
                    value={templateForm.useCase}
                    onChange={(e) => setTemplateForm({ ...templateForm, useCase: e.target.value })}
                    label="Use Case"
                  >
                    <MenuItem value="login" style={{ fontFamily: FONT_FAMILY }}>Login/OTP</MenuItem>
                    <MenuItem value="booking" style={{ fontFamily: FONT_FAMILY }}>Booking Confirmation</MenuItem>
                    <MenuItem value="assignment" style={{ fontFamily: FONT_FAMILY }}>Rider Assignment</MenuItem>
                    <MenuItem value="cancellation" style={{ fontFamily: FONT_FAMILY }}>Booking Cancellation</MenuItem>
                    <MenuItem value="payment" style={{ fontFamily: FONT_FAMILY }}>Payment</MenuItem>
                    <MenuItem value="other" style={{ fontFamily: FONT_FAMILY }}>Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} style={{ fontFamily: FONT_FAMILY }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveTemplate} 
              variant="contained"
              style={{ backgroundColor: MAIN_COLOR, fontFamily: FONT_FAMILY }}
            >
              {t('save_template')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Test Template Dialog */}
        <Dialog 
          open={openTestDialog} 
          onClose={handleCloseTestDialog} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
        >
          <DialogTitle style={{ fontFamily: FONT_FAMILY }}>
            Test Template: {testingTemplate?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" color="textSecondary" style={{ fontFamily: FONT_FAMILY, mb: 1 }}>
                Template ID: {testingTemplate?.templateId}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ fontFamily: FONT_FAMILY, mb: 2 }}>
                Use Case: {testingTemplate?.useCase}
              </Typography>
              
              <TextField
                fullWidth
                label="Phone Number"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                variant="outlined"
                placeholder="+919366744682"
                helperText="Format: +919366744682 or 919366744682 or 9366744682"
                sx={{ mb: 2 }}
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              />
              
              {testResult && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: testResult.includes('✅') ? '#e8f5e8' : '#ffeaea' }}>
                  <Typography variant="body2" style={{ fontFamily: FONT_FAMILY }}>
                    {testResult}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTestDialog} style={{ fontFamily: FONT_FAMILY }}>
              Cancel
            </Button>
            <Button 
              onClick={handleTestTemplate}
              variant="contained"
              disabled={testSending}
              style={{ backgroundColor: MAIN_COLOR, fontFamily: FONT_FAMILY }}
            >
              {testSending ? "Sending..." : "Send Test SMS"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}
